// What a user action changes, as the kit's StoreChanges hands it to a back end: per collection, the records saved
// (new or changed, whole) and the ids deleted, plus the marks the account needs. The rules in repeats.ts, actions.ts
// and the like return these; the data layer turns each into writes (changes.ts: `recordWrite`, `tombstoneWrite`;
// sharing.ts: where each record goes) and keeps them for Undo.

import type { FeatureMark } from "./featureLevel";
import type { Area, InboxTrace, Project, Section, Todo } from "./records";
import type { Day, Weekday } from "./types";
import { newID } from "./ids";

export interface RecordEdits<T extends { id: string }> {
  /** New or changed records, whole: the data layer compares them with what it knew (changes.ts). */
  saved: T[];
  deleted: string[];
}

export interface Edits {
  todos: RecordEdits<Todo>;
  projects: RecordEdits<Project>;
  sections: RecordEdits<Section>;
  areas: RecordEdits<Area>;
  inboxTraces: RecordEdits<InboxTrace>;
  /** Marks the account needs because of this change (featureLevel.ts: `markFields` writes them, only upward). */
  marks: FeatureMark[];
}

export function noEdits(): Edits {
  const none = () => ({ saved: [], deleted: [] });
  return { todos: none(), projects: none(), sections: none(), areas: none(), inboxTraces: none(), marks: [] };
}

export function isNoEdit(edits: Edits): boolean {
  return [edits.todos, edits.projects, edits.sections, edits.areas, edits.inboxTraces]
    .every((records) => records.saved.length === 0 && records.deleted.length === 0) && edits.marks.length === 0;
}

/** Both changes as one, `later` after `earlier`: a record saved twice keeps its last version; a deletion wins. */
export function combineEdits(earlier: Edits, later: Edits): Edits {
  function combine<T extends { id: string }>(a: RecordEdits<T>, b: RecordEdits<T>): RecordEdits<T> {
    const saved = new Map<string, T>();
    for (const record of [...a.saved, ...b.saved]) saved.set(record.id, record);
    const deleted = [...new Set([...a.deleted, ...b.deleted])];
    for (const id of deleted) saved.delete(id);
    return { saved: [...saved.values()], deleted };
  }
  return {
    todos: combine(earlier.todos, later.todos),
    projects: combine(earlier.projects, later.projects),
    sections: combine(earlier.sections, later.sections),
    areas: combine(earlier.areas, later.areas),
    inboxTraces: combine(earlier.inboxTraces, later.inboxTraces),
    marks: [...earlier.marks, ...later.marks],
  };
}

/**
 * What the rules need besides the records: "today" (the browser's date), the moment now, the zone moments are read
 * in (the browser's), the first day of the week (Calendar.firstWeekday: 2 = Monday, 1 = Sunday), and how new ids
 * are made (tests pass a counter).
 */
export interface RuleContext {
  today: Day;
  now: Date;
  zone: string;
  firstWeekday?: Weekday;
  newID?: () => string;
}

export function makeID(context: Pick<RuleContext, "newID">): string {
  return (context.newID ?? newID)();
}
